import React, { useEffect, useState } from 'react';
import {
  Smartphone, Code, Zap, Shield, ArrowRight, Layers, Play,
  Layout, Database, CheckCircle2, Rocket, Globe, Apple, Cpu
} from 'lucide-react';
import SEO from './SEO';

const AppDevelopment = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setIsVisible(true);
  }, []);

  const features = [
    {
      title: "Native iOS Development",
      description: "Pixel-perfect, high-performance applications built specifically for the Apple ecosystem using Swift and SwiftUI.",
      icon: Apple,
      color: "text-white",
      bgGlow: "bg-white/20",
      span: "md:col-span-2 md:row-span-2",
      image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?q=80&w=2070&auto=format&fit=crop"
    },
    {
      title: "Native Android",
      description: "Robust Kotlin architectures optimized for millions of devices globally.",
      icon: Smartphone,
      color: "text-emerald-400",
      bgGlow: "bg-emerald-400/20",
      span: "md:col-span-1 md:row-span-1"
    },
    {
      title: "Cross-Platform",
      description: "Unified React Native & Flutter codebases for rapid scaling.",
      icon: Layers,
      color: "text-blue-400",
      bgGlow: "bg-blue-400/20",
      span: "md:col-span-1 md:row-span-1"
    },
    {
      title: "Immersive UI/UX",
      description: "Award-winning interfaces that maximize user retention and engagement.",
      icon: Layout,
      color: "text-purple-400",
      bgGlow: "bg-purple-400/20",
      span: "md:col-span-1 md:row-span-1"
    },
    {
      title: "Scalable Cloud Backends",
      description: "Serverless architectures, microservices, and secure API gateways powering your app.",
      icon: Database,
      color: "text-rose-400",
      bgGlow: "bg-rose-400/20",
      span: "md:col-span-2 md:row-span-1"
    }
  ];

  const technologies = [
    "SwiftUI", "Kotlin", "React Native", "Flutter",
    "Firebase", "AWS Amplify", "GraphQL", "Node.js", "Docker", "MongoDB"
  ];

  const processSteps = [
    { title: "Product Strategy", desc: "Defining the core loop, audience, and monetization." },
    { title: "Prototyping", desc: "Interactive Figma wireframes and user testing." },
    { title: "Agile Engineering", desc: "Two-week sprints delivering testable builds." },
    { title: "Global Launch", desc: "App Store optimization and seamless deployment." }
  ];

  return (
    <div className="bg-[#030712] min-h-screen text-white pt-20 overflow-hidden selection:bg-[#0EA5E9]/30 selection:text-white" style={{ fontFamily: 'emoji' }}>
      <SEO
        title="Mobile App Development Services – Vishwa DigiTech"
        description="Professional iOS and Android mobile app development in Sangli. Vishwa DigiTech creates high-performance native and cross-platform apps. Get your app built by experts today."
        keywords="mobile app development sangli, android app development india, ios app development india, app development company sangli maharashtra, react native app development"
        canonicalPath="/services/apps"
      />
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(-2deg); }
          50% { transform: translateY(-15px) rotate(2deg); }
        }
        @keyframes float-reverse {
          0%, 100% { transform: translateY(0) rotate(2deg); }
          50% { transform: translateY(15px) rotate(-2deg); }
        }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-float-reverse { animation: float-reverse 8s ease-in-out infinite; }
      `}</style>
      
      {/* Immersive Hero Section */}
      <section className="relative min-h-[95vh] flex items-center justify-center pt-10 border-b border-white/5">
        {/* Dynamic Abstract Background */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-[#0EA5E9]/10 rounded-full blur-[150px] mix-blend-screen animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-[800px] h-[800px] bg-[#1A4A8B]/20 rounded-full blur-[150px] mix-blend-screen" />
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiLz48L3N2Zz4=')] opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#030712]/80 to-[#030712]" />
        </div>

        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full transition-all duration-1000 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`}>
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-sm font-bold tracking-wider uppercase mb-8 backdrop-blur-md">
                <span className="w-2 h-2 rounded-full bg-[#0EA5E9] animate-ping" />
                Mobile Innovation Lab
              </div>
              
              <h1 className="text-6xl md:text-7xl lg:text-8xl font-black leading-[1.1] tracking-tight mb-8">
                Apps That <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">
                  Dominate
                </span><br />
                The Market.
              </h1>
              
              <p className="text-lg md:text-xl text-gray-400 mb-10 max-w-lg leading-relaxed font-light">
                We design and engineer category-defining mobile applications for iOS and Android that users can't live without.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-5">
                <button className="group relative px-8 py-4 bg-white text-[#030712] rounded-2xl font-black tracking-wide overflow-hidden hover:scale-105 transition-all duration-300 shadow-[0_0_30px_rgba(14,165,233,0.3)] hover:shadow-[0_0_40px_rgba(14,165,233,0.5)]">
                  <div className="absolute inset-0 bg-[#0EA5E9] translate-y-[100%] group-hover:translate-y-0 transition-transform duration-300 ease-in-out" />
                  <span className="relative z-10 flex items-center justify-center gap-3 group-hover:text-white transition-colors">
                    Build Your App <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>
                <button className="flex items-center justify-center gap-3 px-8 py-4 rounded-2xl font-bold text-white border border-white/10 bg-white/5 hover:bg-white/10 backdrop-blur-md transition-colors">
                  <Play className="w-5 h-5 text-[#0EA5E9]" /> See Our Work
                </button>
              </div>
            </div>

            {/* Right Visual (Floating Android Mockups) */}
            <div className="relative hidden lg:block h-[650px] perspective-1000">
              <div className="absolute top-0 right-10 w-[280px] h-[580px] rounded-[2rem] bg-[#0A0F1F] border-[6px] border-gray-800 shadow-[0_0_50px_rgba(14,165,233,0.3)] animate-float overflow-hidden z-20">
                 {/* Android Status Bar */}
                 <div className="absolute top-0 inset-x-0 h-7 bg-black/20 z-40 flex justify-between items-center px-4">
                    <span className="text-[10px] text-gray-200 font-medium tracking-wide">10:00</span>
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-gray-950 rounded-full shadow-[inset_0_-1px_2px_rgba(255,255,255,0.1)]" /> {/* Punch Hole */}
                    <div className="flex gap-1.5 items-center">
                       {/* Signal & Battery Icons (Abstract) */}
                       <div className="flex items-end gap-[1px] h-2.5">
                         <div className="w-0.5 h-1 bg-gray-200" /><div className="w-0.5 h-1.5 bg-gray-200" /><div className="w-0.5 h-2 bg-gray-200" /><div className="w-0.5 h-2.5 bg-gray-200" />
                       </div>
                       <div className="w-4 h-2.5 border border-gray-200 rounded-sm p-[1px]"><div className="w-2/3 h-full bg-gray-200" /></div>
                    </div>
                 </div>
                 
                 {/* CSS Built App UI */}
                 <div className="absolute inset-0 pt-10 px-4 bg-[#0A0F1F] z-10">
                    <div className="flex justify-between items-center mb-6 mt-2">
                       <div className="w-10 h-10 rounded-full bg-[#0EA5E9]/20 border border-[#0EA5E9]/50 flex items-center justify-center shadow-[0_0_15px_rgba(14,165,233,0.3)]">
                          <span className="text-[#0EA5E9] font-bold">U</span>
                       </div>
                       <div className="flex gap-1">
                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                       </div>
                    </div>
                    
                    <h3 className="text-white font-bold text-xl mb-1">Dashboard</h3>
                    <p className="text-gray-400 text-[11px] mb-6 font-light">Welcome back, check your stats.</p>
                    
                    <div className="w-full h-32 rounded-2xl bg-gradient-to-tr from-[#0EA5E9]/20 to-[#1A4A8B]/40 border border-[#0EA5E9]/30 mb-4 p-4 flex flex-col justify-between shadow-[0_10px_30px_rgba(14,165,233,0.15)] relative overflow-hidden">
                       <div className="absolute -right-6 -top-6 w-24 h-24 bg-[#0EA5E9]/30 rounded-full blur-xl" />
                       <span className="text-[#0EA5E9] text-[10px] font-bold uppercase tracking-wider relative z-10">Total Revenue</span>
                       <span className="text-white text-3xl font-black relative z-10">$45,231</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 mb-4">
                       <div className="h-28 rounded-[1rem] bg-white/[0.03] border border-white/5 p-3 flex flex-col justify-between hover:bg-white/[0.05] transition-colors">
                          <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center mb-2"><span className="w-2.5 h-2.5 bg-emerald-500 rounded-full shadow-[0_0_10px_rgba(16,185,129,0.5)]" /></div>
                          <div className="flex flex-col">
                            <span className="text-gray-400 text-[10px] uppercase">Active</span>
                            <span className="text-white font-bold text-lg">1,204</span>
                          </div>
                       </div>
                       <div className="h-28 rounded-[1rem] bg-white/[0.03] border border-white/5 p-3 flex flex-col justify-between hover:bg-white/[0.05] transition-colors">
                          <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center mb-2"><span className="w-2.5 h-2.5 bg-purple-500 rounded-full shadow-[0_0_10px_rgba(168,85,247,0.5)]" /></div>
                          <div className="flex flex-col">
                            <span className="text-gray-400 text-[10px] uppercase">Downloads</span>
                            <span className="text-white font-bold text-lg">8,492</span>
                          </div>
                       </div>
                    </div>
                 </div>

                 <div className="absolute bottom-20 left-5 right-5 z-20">
                    <div className="h-12 w-full bg-[#0EA5E9] rounded-xl flex items-center justify-center shadow-[0_10px_20px_rgba(14,165,233,0.3)]">
                       <span className="text-white font-bold text-sm tracking-wide">Install Now</span>
                    </div>
                 </div>
                 
                 {/* Android Navigation Bar */}
                 <div className="absolute bottom-0 inset-x-0 h-12 bg-black/80 backdrop-blur-xl flex items-center justify-around px-8 z-40 border-t border-white/10">
                    <div className="w-4 h-4 border-[2px] border-gray-400 rounded-[3px] bg-transparent" /> {/* Recents */}
                    <div className="w-4 h-4 border-[2px] border-gray-400 rounded-full bg-transparent" /> {/* Home */}
                    <div className="w-0 h-0 border-t-[7px] border-t-transparent border-r-[10px] border-r-gray-400 border-b-[7px] border-b-transparent bg-transparent ml-1" /> {/* Back */}
                 </div>
              </div>
              
              <div className="absolute top-20 left-0 w-[260px] h-[540px] rounded-[2rem] bg-[#0A0F1F] border-[5px] border-gray-800/50 shadow-[0_0_50px_rgba(14,165,233,0.2)] animate-float-reverse overflow-hidden z-10 scale-90 opacity-80 blur-[1px]">
                 {/* Secondary App UI */}
                 <div className="absolute inset-0 pt-12 px-4 bg-[#0A0F1F]">
                    <div className="w-full h-40 rounded-2xl bg-white/[0.02] border border-white/5 mb-4 p-4 flex items-end">
                       <div className="w-full flex items-end justify-between gap-2 h-24">
                          <div className="w-full bg-[#0EA5E9]/40 h-[40%] rounded-t-sm" />
                          <div className="w-full bg-[#0EA5E9]/60 h-[70%] rounded-t-sm" />
                          <div className="w-full bg-[#0EA5E9] h-[100%] rounded-t-sm shadow-[0_0_15px_rgba(14,165,233,0.5)]" />
                          <div className="w-full bg-[#0EA5E9]/50 h-[60%] rounded-t-sm" />
                          <div className="w-full bg-[#0EA5E9]/30 h-[80%] rounded-t-sm" />
                       </div>
                    </div>
                    <div className="space-y-3">
                       <div className="w-full h-12 rounded-xl bg-white/[0.03] border border-white/5" />
                       <div className="w-full h-12 rounded-xl bg-white/[0.03] border border-white/5" />
                       <div className="w-full h-12 rounded-xl bg-white/[0.03] border border-white/5" />
                       <div className="w-full h-12 rounded-xl bg-white/[0.03] border border-white/5" />
                    </div>
                 </div>
                 <div className="absolute inset-0 bg-[#0A0F1F]/40 z-20" />
              </div>
            </div>
            
          </div>
        </div>
      </section>

      {/* Bento Grid Services Section */}
      <section className="py-32 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-20 flex flex-col items-center text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-sm font-bold tracking-wider uppercase mb-8 backdrop-blur-md">
              <Smartphone className="w-4 h-4" />
              Platform Mastery
            </div>
            <h2 className="text-5xl md:text-6xl font-black mb-6 tracking-tight text-white">Platform <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">Mastery</span></h2>
            <p className="text-gray-400 text-xl max-w-2xl font-light">From native performance to unified cross-platform architectures.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-[250px]">
            {features.map((feature, idx) => (
              <div 
                key={idx} 
                className={`group relative overflow-hidden rounded-[2.5rem] bg-[#0A0F1F]/50 border border-white/5 hover:border-[#0EA5E9]/30 hover:bg-[#0A0F1F]/80 hover:shadow-[0_0_30px_rgba(14,165,233,0.15)] backdrop-blur-sm transition-all duration-500 p-8 flex flex-col justify-between ${feature.span}`}
              >
                {feature.image && (
                  <div className="absolute inset-0 opacity-20 group-hover:opacity-40 transition-opacity duration-700">
                    <img src={feature.image} alt={feature.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#030712] via-[#030712]/80 to-transparent" />
                  </div>
                )}
                
                <div className={`absolute -top-10 -right-10 w-64 h-64 ${feature.bgGlow} blur-[80px] rounded-full opacity-0 group-hover:opacity-60 transition-opacity duration-700`} />
                
                <div className="relative z-10">
                  <div className="w-16 h-16 rounded-2xl bg-white/[0.03] border border-white/10 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-white/[0.08] transition-all duration-500 shadow-xl">
                    <feature.icon className={`w-8 h-8 ${feature.color}`} />
                  </div>
                </div>
                
                <div className="relative z-10 mt-auto">
                  <h3 className="text-2xl font-bold text-white mb-3 tracking-wide">{feature.title}</h3>
                  <p className="text-gray-400 text-sm md:text-base leading-relaxed font-light">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack section - Floating Pills */}
      <section className="py-24 border-y border-white/5 bg-gradient-to-b from-[#0A0F1F] to-[#030712] relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4wNykiLz48L3N2Zz4=')] opacity-30" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-sm font-bold tracking-wider uppercase mb-8 backdrop-blur-md">
            <Cpu className="w-4 h-4" />
            Tech Ecosystem
          </div>
          
          <div className="flex flex-wrap justify-center gap-4 max-w-5xl mx-auto">
            {technologies.map((tech, idx) => (
              <div 
                key={idx} 
                className="px-8 py-4 rounded-full bg-[#0A0F1F] border border-white/10 text-gray-300 font-bold tracking-wide hover:text-white hover:bg-[#0EA5E9]/20 hover:border-[#0EA5E9]/50 hover:-translate-y-2 hover:shadow-[0_10px_40px_rgba(14,165,233,0.3)] transition-all duration-300 cursor-default"
              >
                {tech}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modern Development Pipeline */}
      <section className="py-32 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-20 items-center">
            
            <div className="lg:w-1/2 relative w-full order-2 lg:order-1">
               <div className="absolute inset-0 bg-gradient-to-tr from-[#0EA5E9]/20 to-[#1A4A8B]/20 rounded-full blur-[120px]" />
               <img src="https://images.unsplash.com/photo-1551650975-87deedd944c3?q=80&w=1974&auto=format&fit=crop" alt="Mobile Dev" className="relative z-10 rounded-[3rem] border border-white/10 shadow-[0_0_40px_rgba(14,165,233,0.15)] grayscale hover:grayscale-0 transition-all duration-1000 object-cover w-full h-[600px]" />
            </div>

            <div className="lg:w-1/2 order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-sm font-bold tracking-wider uppercase mb-6 backdrop-blur-md">
                <Layers className="w-4 h-4" />
                Our Process
              </div>
              <h2 className="text-5xl md:text-6xl font-black mb-8 leading-tight tracking-tight">
                Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">Process</span>
              </h2>
              <p className="text-gray-400 text-xl mb-12 leading-relaxed font-light">
                From napkin sketches to Top Charts. Our methodology is designed to reduce risk, iterate fast, and launch with maximum impact.
              </p>
              
              <div className="space-y-6 relative before:absolute before:inset-0 before:ml-7 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-1 before:bg-gradient-to-b before:from-[#0EA5E9] before:via-[#0EA5E9] before:to-transparent before:rounded-full">
                {processSteps.map((step, idx) => (
                  <div key={idx} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group">
                    <div className="flex items-center justify-center w-14 h-14 rounded-full border-4 border-[#030712] bg-[#0EA5E9] text-white font-black text-xl shadow-[0_0_30px_rgba(14,165,233,0.6)] shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10 group-hover:scale-110 transition-transform">
                      {idx + 1}
                    </div>
                    <div className="w-[calc(100%-5rem)] md:w-[calc(50%-4rem)] p-8 rounded-3xl bg-white/[0.02] border border-white/5 group-hover:bg-white/[0.06] group-hover:border-[#0EA5E9]/40 group-hover:shadow-[0_0_40px_rgba(14,165,233,0.15)] transition-all duration-300">
                      <h4 className="text-2xl font-bold text-white mb-2 group-hover:text-[#0EA5E9] transition-colors">{step.title}</h4>
                      <p className="text-gray-400 text-base leading-relaxed font-light">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Epic CTA */}
      <section className="py-32 relative overflow-hidden border-t border-white/5 bg-[#0A0F1F]/30">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl h-full max-h-[400px] bg-[#0EA5E9] rounded-full blur-[200px] opacity-10 pointer-events-none" />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-6xl md:text-8xl font-black text-white mb-8 tracking-tighter">Ready to <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">Launch?</span></h2>
          <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto font-light leading-relaxed">
            Your users are waiting for the next big thing. Let's build a mobile application that redefines your industry.
          </p>
          <button className="group relative px-12 py-6 bg-white text-[#030712] rounded-full font-black text-xl overflow-hidden hover:scale-105 transition-all duration-300 shadow-[0_0_40px_rgba(14,165,233,0.3)] hover:shadow-[0_0_60px_rgba(14,165,233,0.5)]">
            <div className="absolute inset-0 bg-[#0EA5E9] translate-y-[100%] group-hover:translate-y-0 transition-transform duration-300 ease-in-out" />
            <span className="relative z-10 group-hover:text-white transition-colors">Start Your App</span>
          </button>
        </div>
      </section>

    </div>
  );
};

export default AppDevelopment;
